import { useMemo } from 'react';
import useDashboardData, { resolveDateRange, browserEventsStart } from './hooks/useDashboardData';
import { mapSimEvent, mapCourseEvent, mapReminderEvent } from './data';
import { hasTenancyScope } from './serverScope';

const MAPPERS = { simulations: mapSimEvent, courses: mapCourseEvent, reminders: mapReminderEvent };

// The scope to read events from the server with, or null to use the browser's
// log. A child-tenancy filter always goes to the server; so does a window that
// starts before the browser's 13 months (BD-1124: a 2024 custom range exported
// only from late 2025). selfScope is the dashboard's own-tenancy scope.
export function serverEventsScope({ scope, selfScope, period, startDate, endDate }) {
    if (hasTenancyScope(scope)) return { tenancy: Array.isArray(scope.tenancy) ? scope.tenancy : [scope.tenancy] };
    if (selfScope && resolveDateRange({ period, startDate, endDate }).start < browserEventsStart()) return selfScope;
    return null;
}

function useTenancyEvents(metric, { scope, selfScope, period, startDate, endDate }) {
    const serverScope = serverEventsScope({ scope, selfScope, period, startDate, endDate });
    // `scoped` = rows come from the server rather than the browser log.
    const scoped = serverScope != null;
    const q = useDashboardData(
        { metric, aggregation: 'events', period, startDate, endDate, scope: serverScope || undefined },
        { skip: !scoped },
    );
    const records = q.data?.records;
    const rows = useMemo(() => {
        if (!scoped || !Array.isArray(records)) return null;
        const out = records.map(MAPPERS[metric]).filter((e) => e.dateSent);
        out.sort((a, b) => (a.dateSent < b.dateSent ? 1 : -1));
        return out;
    }, [scoped, records, metric]);
    return {
        scoped, rows,
        isLoading: scoped && !Array.isArray(records) && (q.isFetching || q.isLoading),
        isFetching: scoped && q.isFetching,
        isError: scoped && q.isError,
        refetch: q.refetch,
    };
}

export function useTenancySimEvents(args) {
    return useTenancyEvents('simulations', args);
}

export function useTenancyCourseEvents(args) {
    return useTenancyEvents('courses', args);
}

function useUserEventRows(metric, { email, period, startDate, endDate, skip }) {
    const q = useDashboardData(
        { metric, aggregation: 'events', period, startDate, endDate, scope: { user: [email] } },
        { skip },
    );
    const records = q.data?.records;
    return useMemo(() => {
        if (skip || !Array.isArray(records)) return null;
        return records.map(MAPPERS[metric]).filter((e) => e.dateSent);
    }, [skip, records, metric]);
}

export function useUserEvents({ email, period, startDate, endDate, skip = false }) {
    const args = { email, period, startDate, endDate, skip };
    return {
        sims: useUserEventRows('simulations', args),
        courses: useUserEventRows('courses', args),
        reminders: useUserEventRows('reminders', args),
    };
}
